import React, { useState, useEffect } from 'react';
import { QueryString, isPlainObject } from '@grandvisionhq/utils';

function OneTrustCookieConsent({ locale , src , domain  }) {
    if (!src || !domain) {
        console.error(`Unable to initiate OneTrust with {${JSON.stringify({
            src,
            domain
        })}}`);
        return null;
    }
    const [language] = locale.split('-');
    return(/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("script", {
        type: "text/javascript",
        charSet: "UTF-8",
        crossOrigin: "anonymous",
        "data-language": language,
        "data-domain-script": domain,
        src: src
    }), /*#__PURE__*/ React.createElement("script", {
        type: "text/javascript"
    }, 'function OptanonWrapper() {}')));
}

const MAX_TRY = 30;
let timer;
const handlers = [];
let tryCount = 0;
function handleConsentChange() {
    for(let i = 0; i < handlers.length; i++){
        try {
            handlers[i]();
        } catch (e) {}
    }
}
function onConsentChange(handler) {
    clearInterval(timer);
    if (!handlers.includes(handler)) {
        handlers.push(handler);
    }
    if (window.Optanon) {
        window.Optanon.OnConsentChanged(handleConsentChange);
        return;
    }
    timer = setInterval(()=>{
        if (window.Optanon) {
            clearInterval(timer);
            window.Optanon.OnConsentChanged(handleConsentChange);
        } else {
            ++tryCount;
            if (tryCount > MAX_TRY) {
                clearInterval(timer);
            }
        }
    }, 1000);
}

function BypassProvider() {
    return new Set([
        'required',
        'analytics',
        'functional'
    ]);
}

/*! js-cookie v3.0.1 | MIT */ /* eslint-disable no-var */ function assign(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i];
        for(var key in source){
            target[key] = source[key];
        }
    }
    return target;
}
/* eslint-enable no-var */ /* eslint-disable no-var */ var defaultConverter = {
    read: function(value) {
        if (value[0] === '"') {
            value = value.slice(1, -1);
        }
        return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
    },
    write: function(value) {
        return encodeURIComponent(value).replace(/%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g, decodeURIComponent);
    }
};
/* eslint-enable no-var */ /* eslint-disable no-var */ function init(converter1, defaultAttributes) {
    function set(key, value, attributes) {
        if (typeof document === 'undefined') {
            return;
        }
        attributes = assign({}, defaultAttributes, attributes);
        if (typeof attributes.expires === 'number') {
            attributes.expires = new Date(Date.now() + attributes.expires * 86400000);
        }
        if (attributes.expires) {
            attributes.expires = attributes.expires.toUTCString();
        }
        key = encodeURIComponent(key).replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent).replace(/[()]/g, escape);
        var stringifiedAttributes = '';
        for(var attributeName in attributes){
            if (!attributes[attributeName]) {
                continue;
            }
            stringifiedAttributes += '; ' + attributeName;
            if (attributes[attributeName] === true) {
                continue;
            }
            // Considers RFC 6265 section 5.2:
            // ...
            // 3.  If the remaining unparsed-attributes contains a %x3B (";")
            //     character:
            // Consume the characters of the unparsed-attributes up to,
            // not including, the first %x3B (";") character.
            // ...
            stringifiedAttributes += '=' + attributes[attributeName].split(';')[0];
        }
        return document.cookie = key + '=' + converter1.write(value, key) + stringifiedAttributes;
    }
    function get(key) {
        if (typeof document === 'undefined' || arguments.length && !key) {
            return;
        }
        // To prevent the for loop in the first place assign an empty array
        // in case there are no cookies at all.
        var cookies = document.cookie ? document.cookie.split('; ') : [];
        var jar = {};
        for(var i = 0; i < cookies.length; i++){
            var parts = cookies[i].split('=');
            var value = parts.slice(1).join('=');
            try {
                var foundKey = decodeURIComponent(parts[0]);
                jar[foundKey] = converter1.read(value, foundKey);
                if (key === foundKey) {
                    break;
                }
            } catch (e) {}
        }
        return key ? jar[key] : jar;
    }
    return Object.create({
        set: set,
        get: get,
        remove: function(key, attributes) {
            set(key, '', assign({}, attributes, {
                expires: -1
            }));
        },
        withAttributes: function(attributes) {
            return init(this.converter, assign({}, this.attributes, attributes));
        },
        withConverter: function(converter) {
            return init(assign({}, this.converter, converter), this.attributes);
        }
    }, {
        attributes: {
            value: Object.freeze(defaultAttributes)
        },
        converter: {
            value: Object.freeze(converter1)
        }
    });
}
var api = init(defaultConverter, {
    path: '/'
});

// empty consent
const EmptyConsent = new Set();

const CookieConsentContext = /*#__PURE__*/ React.createContext(EmptyConsent);
CookieConsentContext.displayName = 'CookieConsentContext';
const CookieConsentContextProvider = ({ children , cookies , consentProvider ,  })=>{
    const [cookiesState, setCookiesState] = useState(cookies);
    useEffect(()=>{
        onConsentChange(()=>{
            setCookiesState(api.get());
        });
    }, []);
    return(/*#__PURE__*/ React.createElement(CookieConsentContext.Provider, {
        value: consentProvider(cookiesState)
    }, children));
};
const useCookieConsentContext = ()=>React.useContext(CookieConsentContext)
;

const OneTrustConsentMap = {
    C0001: 'required',
    C0002: 'analytics',
    C0003: 'functional',
    C0004: 'marketing',
    C0005: 'social'
};
const isCookieCategoryEnabled = (category, consentResult)=>consentResult ? consentResult.has(category) : false
;
const isOneTrustCookie = (data)=>isPlainObject(data) && 'groups' in data
;
function getConsentResult(cookies) {
    const consentRawData = (cookies === null || cookies === void 0 ? void 0 : cookies.OptanonConsent) || '';
    if (!consentRawData) {
        return EmptyConsent;
    }
    const parsedValues = QueryString.parse(consentRawData);
    if (!isOneTrustCookie(parsedValues)) {
        return EmptyConsent;
    }
    return new Set(parsedValues.groups.split(',') // => [ 'C0001:1', 'C0002:0', ... ]
    .map((pair)=>pair.split(':')
    ) // => [ ['C0001', '1'], ['C0002', '0'], ... ]
    .filter(([, value])=>+value
    ) // => [ ['C0001', '1'], ... ]
    .map(([key])=>OneTrustConsentMap[key]
    ) // => [ 'required', ... ]
    );
}
const OneTrustProvider = (cookies)=>{
    return getConsentResult(cookies);
};

export { BypassProvider, CookieConsentContextProvider, EmptyConsent, OneTrustCookieConsent, OneTrustProvider, isCookieCategoryEnabled, onConsentChange, useCookieConsentContext };
//# sourceMappingURL=index.js.map
